/**
 * What a position actually blocks — ROADMAP 4.19.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SELLING IS NOT PRICED IN PREMIUM
 *
 * Buying an option costs its premium and nothing else. SELLING one blocks
 * INITIAL MARGIN, which on a NIFTY lot is six figures against a few thousand
 * rupees of premium — so a rupee budget divided by premium sizes a short
 * position roughly fifteen times too large. That is the bug this module
 * exists to remove.
 *
 * WHAT THE EXCHANGE ACTUALLY CHARGES (NSE Clearing, equity derivatives)
 *
 *   initial margin = SPAN + exposure margin (ELM)
 *
 * SPAN is a SCENARIO SCAN, not a percentage. The clearing corporation
 * revalues the whole portfolio under 16 scenarios — the underlying moved by
 * fractions of a Price Scan Range, volatility shifted up and down — and
 * charges the worst outcome. That is why a hedged spread costs a fraction of
 * a naked short: the long leg gains in the scenario where the short loses,
 * and the worst case is netted before it is charged. Nothing in a flat
 * percentage model can reproduce that, which is why this is a real scan.
 *
 *   Price Scan Range, index  = max(6σ × √2, 9.3% of the underlying)
 *   Volatility Scan Range    = max(25% of the volatility, 4 vol points)
 *   Exposure margin, index   = 2% of contract value (spot × units)
 *   Expiry-day extra ELM     = a further 2%, on SHORT INDEX OPTIONS, since
 *                              20 Nov 2024 — and it applies even when the
 *                              position is hedged.
 *
 * The √2 is the two-day margin period of risk the 2020 framework introduced.
 * σ here is the daily volatility implied by the option's own IV, which is
 * what we have; the exchange uses its own EWMA estimate, so the two agree in
 * shape and not to the rupee.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS AN ESTIMATE, AND EVERY CALLER MUST SAY SO
 *
 * The exchange publishes the real parameters daily in a SPAN risk file and
 * this broker's gateway serves no margin endpoint ([B9]: `e-calculatemargin`
 * answers "Data not found" for every input). So the number here is produced
 * by the right METHOD on our own inputs, and it will differ from the
 * broker's by a few percent in either direction.
 *
 * It is therefore deliberately CONSERVATIVE in two places: the net option
 * value credit that SPAN applies is not taken, and sizing callers add a
 * buffer on top. An estimate that is slightly too high costs some unused
 * capital; one that is too low is an order the broker refuses, or worse, a
 * position the trader cannot hold.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { black76Price } from './greeks.js';
/** Trading days in a year, for turning an annual volatility into a daily one. */
const TRADING_DAYS = 252;
/** NSE Clearing's floors for index derivatives. */
export const INDEX_PRICE_SCAN_FLOOR = 0.093;
export const VOL_SCAN_FLOOR = 0.04;
/** Exposure margin, index F&O: 2% of contract value. */
export const INDEX_EXPOSURE_RATE = 0.02;
/** The expiry-day addition on short index options, from 20 Nov 2024. */
export const EXPIRY_DAY_ELM_RATE = 0.02;
const SCENARIOS = [
    ...[0, 1 / 3, -1 / 3, 2 / 3, -2 / 3, 1, -1].flatMap((priceFraction) => [1, -1].map((volFraction) => ({ priceFraction, volFraction, weight: 1 }))),
    { priceFraction: 2, volFraction: 0, weight: 0.35 },
    { priceFraction: -2, volFraction: 0, weight: 0.35 },
];
/** Price one leg under a shifted forward and volatility. */
function valueUnder(leg, forward, vol, years, rate) {
    return black76Price({
        forward,
        strike: leg.strike,
        years,
        rate,
        vol: Math.max(vol, 0.0001),
        optionType: leg.optionType,
    });
}
/**
 * Initial margin for a basket, by the exchange's own method.
 *
 * Returns a REASON rather than a number when it cannot be computed. A leg
 * with no implied volatility cannot be revalued under a scenario, and a
 * margin figure produced without it would be a specific claim about blocked
 * capital with nothing behind it — the same rule §4.8a applies to prices.
 */
export function estimateMargin(input) {
    const { legs, forward, spot, years, rate, isExpiryDay } = input;
    if (legs.length === 0)
        return { ok: false, why: 'no legs' };
    if (!(forward > 0) || !(spot > 0))
        return { ok: false, why: 'no underlying price yet' };
    if (!(years > 0))
        return { ok: false, why: 'this expiry has already settled' };
    for (const leg of legs) {
        if (!(leg.iv > 0)) {
            return { ok: false, why: 'one leg has no implied volatility — margin cannot be estimated' };
        }
        if (!(leg.strike > 0) || !Number.isFinite(leg.netQty)) {
            return { ok: false, why: 'a leg is not priceable' };
        }
    }
    const shortUnits = legs.reduce((a, l) => a + (l.netQty < 0 ? -l.netQty : 0), 0);
    const premium = legs.reduce((a, l) => a + l.price * l.netQty, 0);
    /*
     * A BASKET THAT ONLY BUYS BLOCKS ITS PREMIUM AND NOTHING ELSE. There is
     * no scenario in which a long option loses more than it cost, so the
     * exchange charges no initial margin on one, and running the scan here
     * would report the premium as though it were margin on top of itself.
     */
    if (shortUnits === 0) {
        const total = Math.max(0, Math.ceil(premium));
        return {
            ok: true,
            margin: {
                scanRisk: 0,
                exposure: 0,
                expiryElm: 0,
                premium: total,
                total,
                priceScanRange: 0,
                volScanRange: 0,
            },
        };
    }
    /*
     * The scan ranges. σ comes from the legs' own implied volatility — the
     * largest of them, because the scan has to cover the most volatile thing
     * in the basket rather than its average.
     */
    const iv = Math.max(...legs.map((l) => l.iv));
    const dailySigma = iv / Math.sqrt(TRADING_DAYS);
    const priceScanRange = Math.max(6 * dailySigma * Math.SQRT2, INDEX_PRICE_SCAN_FLOOR);
    const volScanRange = Math.max(0.25 * iv, VOL_SCAN_FLOOR);
    const base = legs.map((l) => valueUnder(l, forward, l.iv, years, rate));
    let worstLoss = 0;
    for (const scenario of SCENARIOS) {
        const shifted = forward * (1 + scenario.priceFraction * priceScanRange);
        let pnl = 0;
        for (const [i, leg] of legs.entries()) {
            const vol = leg.iv + scenario.volFraction * volScanRange;
            pnl += (valueUnder(leg, shifted, vol, years, rate) - base[i]) * leg.netQty;
        }
        // A loss is negative P&L. The weight scales the extreme moves down.
        const loss = pnl < 0 ? -pnl * scenario.weight : 0;
        if (loss > worstLoss)
            worstLoss = loss;
    }
    /*
     * Exposure is charged on the SHORT contracts' notional, hedged or not —
     * it is a liquidity buffer on the obligation, not a risk measure, so a
     * long leg does not reduce it the way it reduces the scan.
     */
    const exposure = INDEX_EXPOSURE_RATE * spot * shortUnits;
    const expiryElm = isExpiryDay ? EXPIRY_DAY_ELM_RATE * spot * shortUnits : 0;
    const scanRisk = Math.ceil(worstLoss);
    return {
        ok: true,
        margin: {
            scanRisk,
            exposure: Math.ceil(exposure),
            expiryElm: Math.ceil(expiryElm),
            premium: Math.round(premium),
            total: Math.ceil(scanRisk + exposure + expiryElm),
            priceScanRange,
            volScanRange,
        },
    };
}
/**
 * The safety buffer on a MARGIN-SIZED order.
 *
 * FOUR PERCENT. The estimate is ours and the refusal would be the broker's,
 * so sizing to the last rupee of an approximation is how a basket gets
 * through four legs and is refused on the fifth — the one failure mode a
 * multi-leg order must not have. Four percent is the owner's call on that
 * trade-off: it was ten, which left a tenth of the capital unused on every
 * order to insure against a gap this model does not actually have.
 */
export const MARGIN_SIZING_BUFFER = 1.04;
/**
 * How many lots a budget covers, given one lot's requirement.
 *
 * Rounds DOWN after the buffer, for the same reason `autoLotsFor` does: a
 * budget is a ceiling.
 */
export function lotsForMargin(budget, marginPerLot) {
    if (!(marginPerLot > 0) || !(budget > 0))
        return 0;
    return Math.floor(budget / (marginPerLot * MARGIN_SIZING_BUFFER));
}
//# sourceMappingURL=margin.js.map